import { useLoginInfo } from '@/components/LoginInfo/provider';
import { TransactionItemComponentType } from '@/context/services/transaction_items/List/TransactionItems.service';
import {
  TransactionItemComponent,
  TransactionItemsColumn,
} from '@/features/transaction_items/List/type';
import { useCustomTransactionTwoFlag } from '@/hooks/feature_flag/custom-transaction-two';
import { useTranslation } from '@/i18n';
import {
  ButtonV2,
  IconHelp,
  TextLink,
  Tooltip,
} from '@moneyforward/ap-frontend-components';
import { ColumnType } from 'antd/es/table/interface';
import classNames from 'classnames';
import { memo, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import styles from './column.module.scss';

const cx = classNames.bind(styles);

type DetailButtonCellProps = {
  record: TransactionItemsColumn;
  onClickDetail: (record: TransactionItemsColumn) => void;
  isReorderingMode?: boolean;
};

const DetailButtonCell = memo(function DetailButtonCell({
  record,
  onClickDetail,
  isReorderingMode,
}: DetailButtonCellProps) {
  const { t } = useTranslation();
  const handleClick = useCallback(() => {
    onClickDetail(record);
  }, [onClickDetail, record]);

  return (
    <div className={cx(styles['detail-cell'])}>
      <ButtonV2
        color='primary'
        size='xs'
        isTransparent
        isDisabled={!!isReorderingMode}
        onClick={handleClick}
      >
        {t('transaction_items.table.detail')}
      </ButtonV2>
    </div>
  );
});

export type Props = {
  onDetailClick?: (id: string) => void;
  isReorderingMode?: boolean;
  customTransactionItemsCount?: number;
};

export const useTransactionItemsColumns = ({
  onDetailClick,
  isReorderingMode,
  customTransactionItemsCount,
}: Props): ColumnType<TransactionItemsColumn>[] => {
  const { currentOffice } = useLoginInfo();
  const { t } = useTranslation();
  const { report_type_id } = useParams();
  const onClickDetail = useCallback(
    (record: TransactionItemsColumn) => {
      if (isReorderingMode || !onDetailClick) return;

      if (record.component_type === TransactionItemComponentType.pulldown) {
        window.location.href = `/report_types/${report_type_id}/transaction_items/${record.id.toString()}`;
      } else {
        onDetailClick(record.id.toString());
      }
    },
    [report_type_id, onDetailClick, isReorderingMode]
  );

  const isCustomTransactionTwoFlag = useCustomTransactionTwoFlag();

  const isCustomTransactionItemColumnsVisible =
    isCustomTransactionTwoFlag &&
    !!currentOffice &&
    (currentOffice.isMoreThanTeamPlan ||
      (customTransactionItemsCount ?? 0) > 0);

  return useMemo(
    () => [
      {
        title: t('transaction_items.table.label'),
        dataIndex: 'label',
        key: 'label',
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        render(value: any, record: any) {
          const tooltipInnerHTML = { __html: record.system_tooltip };

          return (
            <div className={cx(styles['visibility-container'])}>
              {value}
              {record.system_tooltip ? (
                <Tooltip
                  title={
                    <span
                      // backend provides trusted HTML (e.g., line breaks, <br />)
                      dangerouslySetInnerHTML={tooltipInnerHTML}
                    />
                  }
                  placement='top'
                  wrapSpanTag={false}
                >
                  <div className={cx(styles['tooltip-container'])}>
                    <IconHelp size={14} />
                  </div>
                </Tooltip>
              ) : null}
            </div>
          );
        },
      },

      ...(isCustomTransactionItemColumnsVisible
        ? [
            {
              title: t('transaction_items.table.customizable'),
              dataIndex: 'customizable',
              key: 'customizable',
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              render(value: any) {
                return value
                  ? t('transaction_items.table.custom')
                  : t('transaction_items.table.master');
              },
              width: 112,
            },
          ]
        : []),

      {
        title: t('transaction_items.table.component_type'),
        dataIndex: 'component_type',
        key: 'component_type',
        width: 160,
        render(value: keyof typeof TransactionItemComponent, record) {
          const isCustomizableDate =
            record.customizable &&
            record.component_type === TransactionItemComponentType.date;

          if (isCustomizableDate && record.date_format_type) {
            return t(
              `transaction_items.table.date_format_type.${record.date_format_type}`
            );
          }

          return t(TransactionItemComponent[value]);
        },
      },
      {
        title: t('transaction_items.table.ref_label'),
        dataIndex: 'ref_label',
        key: 'ref_label',
        render(value, record) {
          return value ? (
            <TextLink href={record.ref_link} target='_blank'>
              {value}
            </TextLink>
          ) : (
            '--'
          );
        },
        width: 160,
      },

      ...(isCustomTransactionItemColumnsVisible
        ? [
            {
              title: t('transaction_items.table.is_visible'),
              dataIndex: 'is_visible',
              key: 'is_visible',
              width: 80,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              render(value: any) {
                return value
                  ? t('transaction_items.table.visible')
                  : t('transaction_items.table.invisible');
              },
            },
          ]
        : []),

      ...(isCustomTransactionItemColumnsVisible
        ? [
            {
              title: ' ',
              dataIndex: 'detail',
              key: 'detail',
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              render(_: any, record: any) {
                return record.customizable ? (
                  <DetailButtonCell
                    record={record}
                    onClickDetail={onClickDetail}
                    isReorderingMode={isReorderingMode}
                  />
                ) : null;
              },
              width: 80,
            },
          ]
        : []),
    ],
    [t, isReorderingMode, onClickDetail, isCustomTransactionItemColumnsVisible]
  );
};
